import React from 'react';

export interface MechanicalButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'blueprint' | 'outline';
  size?: 'default' | 'sm' | 'lg';
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  children: React.ReactNode;
}

export const MechanicalButton: React.FC<MechanicalButtonProps> = ({
  variant = 'secondary',
  size = 'default',
  icon,
  iconPosition = 'left',
  className = '',
  disabled,
  children,
  ...props
}) => {
  // All variants strictly enforce min-h-[44px] and min-w-[44px] for shop-floor mobile touch usability
  const variantStyles = {
    primary:
      'bg-safety-amber text-shop-darkest border border-safety-amberDark shadow-[0_3px_0_0_#92400e,0_4px_6px_rgba(0,0,0,0.4)] hover:bg-[#fbbf24] active:translate-y-0.5 active:shadow-[0_1px_0_0_#92400e,inset_0_2px_4px_rgba(0,0,0,0.3)]',
    secondary:
      'bg-shop-panel text-shop-highlight border border-shop-border shadow-[0_3px_0_0_#0d1117,0_4px_6px_rgba(0,0,0,0.4)] hover:bg-shop-well hover:border-shop-steel active:translate-y-0.5 active:shadow-[0_1px_0_0_#0d1117,inset_0_2px_4px_rgba(0,0,0,0.4)]',
    danger:
      'bg-red-600 text-white border border-[#b91c1c] shadow-[0_3px_0_0_#7f1d1d,0_4px_6px_rgba(0,0,0,0.4)] hover:bg-red-700 active:translate-y-0.5 active:shadow-[0_1px_0_0_#7f1d1d,inset_0_2px_4px_rgba(0,0,0,0.4)]',
    blueprint:
      'bg-safety-blue text-white border border-[#1d4ed8] shadow-[0_3px_0_0_#1e3a8a,0_4px_6px_rgba(0,0,0,0.4)] hover:bg-[#1d4ed8] active:translate-y-0.5 active:shadow-[0_1px_0_0_#1e3a8a,inset_0_2px_4px_rgba(0,0,0,0.4)]',
    outline:
      'bg-shop-dark text-shop-light border border-shop-border shadow-sm hover:bg-shop-panel hover:text-shop-highlight active:translate-y-0.5',
  }[variant];

  const sizeStyles = {
    sm: 'min-h-[44px] min-w-[44px] px-3 py-1.5 text-xs',
    default: 'min-h-[44px] min-w-[44px] px-4 py-2 text-sm',
    lg: 'min-h-[48px] min-w-[48px] px-6 py-3 text-base',
  }[size];

  return (
    <button
      className={`inline-flex items-center justify-center gap-2 font-bold uppercase tracking-wider rounded transition-all duration-100 select-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none ${variantStyles} ${sizeStyles} ${className}`}
      disabled={disabled}
      {...props}
    >
      {icon && iconPosition === 'left' && <span className="shrink-0">{icon}</span>}
      <span>{children}</span>
      {icon && iconPosition === 'right' && <span className="shrink-0">{icon}</span>}
    </button>
  );
};
